import { useQuery, useInfiniteQuery, useMutation } from "@tanstack/react-query";
import { QueryModule } from "@/data/queryModules";
import { MutationEffectsOptions, useMutationEffects } from "@/data/useMutationEffects";
import { AppQueryOptions, AppInfiniteQueryOptions, AppMutationOptions } from "@povio/openapi-codegen-cli";
import { CommonModels } from "@/data/common/common.models";
import { ProjectsModels } from "./projects.models";
import { ProjectsApi } from "./projects.api";

export namespace ProjectsQueries {

export const moduleName = QueryModule.Projects;

export const keys = {
    all: [moduleName] as const,
    get: (limit?: number, order?: string, filter?: CommonModels.ProjectPaginationFilterDto, page?: number, cursor?: string, ) => [...keys.all, "/projects", limit, order, filter, page, cursor] as const,
    getInfinite: (limit?: number, order?: string, filter?: CommonModels.ProjectPaginationFilterDto, cursor?: string, ) => [...keys.all, "/projects", "infinite", limit, order, filter, cursor] as const,
    getFeatured: () => [...keys.all, "/projects/featured", ] as const,
    show: (id: string, ) => [...keys.all, "/projects/:id", id] as const,
    getMedia: (projectId: string, ) => [...keys.all, "/projects/:projectId/media", projectId] as const,
};


 /** 
 * Query `useGet`
 * @summary Get projects
 * @param { number } object.limit Query parameter. Items per response. Minimum: `1`. Maximum: `100`. Default: `10`
 * @param { string } object.order Query parameter. Order by fields (comma separated with +/- prefix): title, description, featured, createdAt, updatedAt. Example: `title`
 * @param { CommonModels.ProjectPaginationFilterDto } object.filter Query parameter
 * @param { number } object.page Query parameter. 1-indexed page number to begin from
 * @param { string } object.cursor Query parameter. ID of item to start after
 * @param { AppQueryOptions } options Query options
 * @returns { UseQueryResult<ProjectsModels.ProjectsGetResponse> } 
 * @statusCodes [200, 401]
 */
export const useGet = <TData>({ limit, order, filter, page, cursor }: { limit: number, order?: string, filter?: CommonModels.ProjectPaginationFilterDto, page?: number, cursor?: string,  }, options?: AppQueryOptions<typeof ProjectsApi.get, TData>) => {
  
  
  return useQuery({
    queryKey: keys.get(limit, order, filter, page, cursor),
    queryFn: () =>  
    
    ProjectsApi.get(limit, order, filter, page, cursor)
    ,
    ...options,
  });
};



/** 
 * Infinite query `useGetInfinite
 * @summary Get projects
 * @param { number } object.limit Query parameter. Items per response. Minimum: `1`. Maximum: `100`. Default: `10`
 * @param { string } object.order Query parameter. Order by fields (comma separated with +/- prefix): title, description, featured, createdAt, updatedAt. Example: `title`
 * @param { CommonModels.ProjectPaginationFilterDto } object.filter Query parameter
 * @param { number } object.page Query parameter. 1-indexed page number to begin from
 * @param { string } object.cursor Query parameter. ID of item to start after
 * @param { AppInfiniteQueryOptions } options Infinite query options
 * @returns { UseInfiniteQueryResult<ProjectsModels.ProjectsGetResponse> } 
 * @statusCodes [200, 401]
 */
export const useGetInfinite = <TData>({ limit, order, filter, cursor }: { limit: number, order?: string, filter?: CommonModels.ProjectPaginationFilterDto, cursor?: string,  }, options?: AppInfiniteQueryOptions<typeof ProjectsApi.get, TData>) => {
  

  return useInfiniteQuery({
    queryKey: keys.getInfinite(limit, order, filter, cursor),
    queryFn: ({ pageParam }) =>  
    
    ProjectsApi.get(limit, order, filter, pageParam, cursor)
    ,
    initialPageParam: 1,
    getNextPageParam: ({ page, totalItems, limit: limitParam }) => {
      const pageParam = page ?? 1;
      return pageParam * limitParam < totalItems ? pageParam + 1 : null;
    },
    ...options,
  });
};




 /** 
 * Mutation `useCreate`
 * @summary Create project
 * @param { ProjectsModels.CreateProjectDto } mutation.data Body parameter
 * @param { AppMutationOptions & MutationEffectsOptions } options Mutation options
 * @returns { UseMutationResult<CommonModels.ProjectDto> } 
 * @statusCodes [201, 401]
 */
export const useCreate = (options?: AppMutationOptions<typeof ProjectsApi.create, { data: ProjectsModels.CreateProjectDto,  }> & MutationEffectsOptions) => {
  
  const { runMutationEffects } = useMutationEffects({ currentModule: moduleName });

  return useMutation({
    mutationFn: ( { data } ) => 
      
            ProjectsApi.create(data)
    ,
    ...options, 
    onSuccess: async (resData, variables, onMutateResult, context) => {
      await runMutationEffects(resData, options);
      options?.onSuccess?.(resData, variables, onMutateResult, context);
    },
  });
};





 /** 
 * Query `useGetFeatured`
 * @summary Get featured project
 * @param { AppQueryOptions } options Query options
 * @returns { UseQueryResult<CommonModels.ProjectWithMediaDto> } 
 * @statusCodes [200, 401, default]
 */
export const useGetFeatured = <TData>(options?: AppQueryOptions<typeof ProjectsApi.getFeatured, TData>) => {
  
  
  return useQuery({
    queryKey: keys.getFeatured(),
    queryFn: ProjectsApi.getFeatured
    ,
    ...options,
  });
};








 /** 
 * Mutation `useRequestUpload`
 * @summary Request upload of a media file for projects
 * @param { ProjectsModels.MediaUploadFileRequest } mutation.data Body parameter
 * @param { AppMutationOptions & MutationEffectsOptions } options Mutation options
 * @returns { UseMutationResult<CommonModels.MediaUploadInstructions> } 
 * @statusCodes [201, 401, default]
 */
export const useRequestUpload = (options?: AppMutationOptions<typeof ProjectsApi.requestUpload, { data: ProjectsModels.MediaUploadFileRequest,  }> & MutationEffectsOptions) => {
  
  const { runMutationEffects } = useMutationEffects({ currentModule: moduleName });

  return useMutation({
    mutationFn: ( { data } ) => 
      
            ProjectsApi.requestUpload(data)
    ,
    ...options, 
    onSuccess: async (resData, variables, onMutateResult, context) => {
      await runMutationEffects(resData, options);
      options?.onSuccess?.(resData, variables, onMutateResult, context);
    },
  });
};





 /** 
 * Query `useShow`
 * @summary Get project by id
 * @param { string } object.id Path parameter
 * @param { AppQueryOptions } options Query options
 * @returns { UseQueryResult<CommonModels.ProjectDto> } 
 * @statusCodes [200, 401]
 */
export const useShow = <TData>({ id }: { id: string,  }, options?: AppQueryOptions<typeof ProjectsApi.show, TData>) => {
  
  
  return useQuery({
    queryKey: keys.show(id),
    queryFn: () =>  
    
    ProjectsApi.show(id)
    ,
    ...options,
  });
};








 /** 
 * Mutation `useUpdate`
 * @summary Update project
 * @param { string } mutation.id Path parameter
 * @param { ProjectsModels.UpdateProjectDto } mutation.data Body parameter
 * @param { AppMutationOptions & MutationEffectsOptions } options Mutation options
 * @returns { UseMutationResult<CommonModels.ProjectDto> } 
 * @statusCodes [200, 401]
 */
export const useUpdate = (options?: AppMutationOptions<typeof ProjectsApi.update, { id: string, data: ProjectsModels.UpdateProjectDto,  }> & MutationEffectsOptions) => {
  
  const { runMutationEffects } = useMutationEffects({ currentModule: moduleName });

  return useMutation({
    mutationFn: ( { id, data } ) => 
      
            ProjectsApi.update(id, data)
    ,
    ...options, 
    onSuccess: async (resData, variables, onMutateResult, context) => {
      const { id } = variables;
      const updateKeys = [keys.show(id), ];
      await runMutationEffects(resData, options, updateKeys);
      options?.onSuccess?.(resData, variables, onMutateResult, context);
    },
  });
};







 /** 
 * Mutation `useDeleteProjectsById`
 * @summary Delete project
 * @param { string } mutation.id Path parameter
 * @param { AppMutationOptions & MutationEffectsOptions } options Mutation options
 * @returns { UseMutationResult<CommonModels.ProjectDto> } 
 * @statusCodes [200, 401]
 */
export const useDeleteProjectsById = (options?: AppMutationOptions<typeof ProjectsApi.deleteProjectsById, { id: string,  }> & MutationEffectsOptions) => {
  
  const { runMutationEffects } = useMutationEffects({ currentModule: moduleName });

  return useMutation({
    mutationFn: ( { id } ) => 
      
            ProjectsApi.deleteProjectsById(id)
    ,
    ...options, 
    onSuccess: async (resData, variables, onMutateResult, context) => {
      const { id } = variables;
      const updateKeys = [keys.show(id), ];
      await runMutationEffects(resData, options, updateKeys);
      options?.onSuccess?.(resData, variables, onMutateResult, context);
    },
  });
};





 /** 
 * Query `useGetMedia`
 * @summary Get media for projects
 * @param { string } object.projectId Path parameter
 * @param { AppQueryOptions } options Query options
 * @returns { UseQueryResult<ProjectsModels.ProjectsGetMediaResponse> } 
 * @statusCodes [200, 401]
 */
export const useGetMedia = <TData>({ projectId }: { projectId: string,  }, options?: AppQueryOptions<typeof ProjectsApi.getMedia, TData>) => {
  
  
  return useQuery({
    queryKey: keys.getMedia(projectId),
    queryFn: () =>  
    
    ProjectsApi.getMedia(projectId)
    ,
    ...options,
  });
};








 /** 
 * Mutation `useDeleteMedia`
 * @summary Delete media for projects
 * @param { string } mutation.projectId Path parameter
 * @param { string } mutation.mediaId Path parameter
 * @param { AppMutationOptions & MutationEffectsOptions } options Mutation options
 * @returns { UseMutationResult<ProjectsModels.ProjectMediaDeletedDto> } 
 * @statusCodes [200, 401]
 */
export const useDeleteMedia = (options?: AppMutationOptions<typeof ProjectsApi.deleteMedia, { projectId: string, mediaId: string,  }> & MutationEffectsOptions) => {
  
  const { runMutationEffects } = useMutationEffects({ currentModule: moduleName });

  return useMutation({
    mutationFn: ( { projectId, mediaId } ) => 
      
            ProjectsApi.deleteMedia(projectId, mediaId)
    ,
    ...options, 
    onSuccess: async (resData, variables, onMutateResult, context) => {
      await runMutationEffects(resData, options);
      options?.onSuccess?.(resData, variables, onMutateResult, context);
    },
  });
};





}
